<template>
    <div>
        <template v-if="shouldShowSinglePlan">
            <div class="row wrapper">
                <h4 class="col xs-12 margin-top payment-plan-title">{{ paymentPlanTitle }}</h4>
            </div>
            <div v-if="missingPaymentTypeError" class="row wrapper selected-payment-type-error">
                <p class="col xs-12 dark-blue-text">{{ missingPaymentTypeError }}</p>
            </div>
        </template>
        <template v-else>
            <div>
                <div class="wrapper">
                    <h3 v-if="!baselinePhoenixChanges" class="col xs-12 margin-top payment-plan-title"> {{ checkoutContent.selectAPaymentPlan }}</h3>
                    <h3 v-else class="col xs-12 margin-top payment-plan-title"> {{ renewalsPersistedVariables.jsonContent.text.confirmPaymentPlan }}</h3>
                </div>
                <!-- toggle button -->
                <div v-if="isPhoenixPhaseTwoCustomer">
                    <div class="flex m-1 justify-center toggle-button">
                        <button @click="handleTermChange(36)" :class="renewalTerm === 36 ? 'toggle-button-clicked' : 'toggle-button-left'">36 Months Term</button>
                        <button @click="handleTermChange(12)" :class="renewalTerm === 12 ? 'toggle-button-clicked' : 'toggle-button-right'">12 Months Term</button>
                    </div>
                </div>  
                <div v-if="showContractDisclaimer" class="col xs-12 bold" id="terms-remaining">
                    {{ checkoutContent.selectPaymentInfo.terms_remaining }} {{ licenseTerm }}
                    <p id="contract-disclaimer" class="mt-0 mb-2">{{ contractDisclaimer }}</p>
                </div>
            </div>
            <div class="row option-container">
                <PaymentPlanTile 
                    v-for="(plan) in approvedPaymentPlanOptions"
                    :key="plan"
                    :id="paymentPlanOptions[plan].paymentHeader"
                    :paymentPlan="plan"
                    :paymentHeader="paymentHeader(plan)"
                    :paymentSubHeader="paymentSubHeader()"
                    :monthlyCost="paymentPlanOptions[plan].monthlyCost"
                    :monthlyListPriceCost="calculateMonthlyListPriceCost"
                    :breakdown="paymentPlanOptions[plan].breakdown"
                    :discount="paymentPlanOptions[plan].discount"
                    :totalSavings="calculateTotalSavings(plan, approvedPaymentPlanOptions.length)"
                    @scrollToPricingBreakdown="() => $emit('scrollToPricingBreakdown')"
                />
            </div>
            <div v-if="clickpathCotermingEnabled">
                <div id='payment-notice' v-html="renewalPaymentNotice()"></div>
            </div>
            <div v-else-if="selectedPaymentType" class="row">
                <p id='payment-notice'>{{ paymentNotice }}</p>
            </div>
            
        </template>
    </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import PaymentPlanTile from './payment-plan-tile';
import { annualTermLength } from '../../../settings';
import { SFDCPaymentTypeToVariableName } from '../../../../corpweb_globals'
import Notice from '../../../components/utils/notice'
import axios from 'axios';
import formatCurrency from '../webstore/mixins/formatCurrency';
import { analyticsTrack } from '../../../plugins/segment';
import RenewalsResources from '../../../mixins/renewals-resources';
export default {

    components: { PaymentPlanTile, Notice },
    mixins: [formatCurrency, RenewalsResources],

    data() {
        return {
            expressAnnualDiscount: 10.0,
            expressUpfrontDiscount: 15.0,
            missingPaymentTypeError: false,
            paymentPlanOptions: {
                'Direct Monthly': {
                    'paymentHeader': 'billed_monthly',
                    'monthlyCost': null,
                    'breakdown': null,
                    'cost': null
                },
                'Direct Annual': {
                    'paymentHeader': 'billed_annually',
                    'monthlyCost': null,
                    'breakdown': null,
                    'discount': null,
                    'cost': null
                },
                'Upfront Payments': {
                    'paymentHeader': 'billed_upfront',
                    'monthlyCost': null,
                    'breakdown': null,
                    'discount': null,
                    'cost': null
                },
            }
        }
    },
    computed: {
        ...mapGetters([
            'currencySymbol',
            'shouldShowSinglePlan',
            'isAccountCreatedAfterTitan',
            'isPartnerAccount',
            'isPhoenixPhaseTwoCustomer',
            'applicationVersion',
            'typeOfRenewal',
            'productSource',
            'isPhoenixPhaseThreeCustomer',
            'clickpathCotermingEnabled'
        ]),
        ...mapGetters('content', [
            'checkoutContent',
            'contractDisclaimer',
            'renewalsPersistedVariables'
        ]),
        ...mapGetters('checkout', [
            'accountId',
            'opptyId',
            'licenseTerm',
            'checkoutType',
            'isRenewal',
            'smallFleetOpportunity',
            'currencyIsoCode',
            'selectedPaymentType',
            'initialPaymentTerm',
            'approvedPaymentType',
            'isSelfServiced',
            'showContractDisclaimer',
            'sentPaymentTypeErrorEmail',
            'renewalTerm',
            'contract'
        ]),
        ...mapGetters('shoppingCart', [
            'productCostsInfo',
            'isSoftwareCartEmpty',
        ]),

        approvedPaymentPlanOptions() {
            let approvedPaymentPlanOptions = ['Direct Monthly', 'Direct Annual', 'Upfront Payments'];
            if (this.phoenixExptChanges){
                return this.renewalTerm === 36 ? ['Upfront Payments', 'Direct Annual', 'Direct Monthly'] : ['Upfront Payments'];
            }
            else if(this.licenseTerm <= 12 && this.isRenewal){
                approvedPaymentPlanOptions = approvedPaymentPlanOptions.filter(item => item !== "Direct Annual")
            }
            let index = approvedPaymentPlanOptions.indexOf(this.approvedPaymentType);
            return approvedPaymentPlanOptions.splice(index, approvedPaymentPlanOptions.length);
        },

        paymentPlanTitle() {
            if (this.isSoftwareCartEmpty) {
                return this.checkoutContent.selectPaymentInfo['upfront_payment_breakdown'];
            } else if (this.smallFleetOpportunity) {
                if(this.isAccountCreatedAfterTitan){
                    return this.checkoutContent.selectPaymentInfo['upfront_payment_breakdown'];
                }
                return this.checkoutContent.selectPaymentInfo['annual_payment_breakdown'];

            } else if (this.checkoutType === "ExpressCheckout" && this.initialPaymentTerm === 'Net 30' && this.currencyIsoCode === 'USD') {
                return `${this.checkoutContent.selectPaymentInfo['annual_payment_breakdown']} - ${this.formatDiscountSavingsText(this.expressAnnualDiscount)}`;

            } else if (this.selectedPaymentType === 'Direct Annual') {
                return this.checkoutContent.selectPaymentInfo['annual_payment_breakdown'];

            } else if (this.selectedPaymentType === 'Direct Monthly') {
                return this.checkoutContent.selectPaymentInfo['monthly_payment_breakdown'];

            } else if (this.selectedPaymentType === 'Upfront Payments') {
                return this.checkoutContent.selectPaymentInfo['upfront_payment_breakdown'];
            }
        },

        paymentNotice() {
            return this.selectedPaymentType === 'Upfront Payments' ? this.checkoutContent.upfrontPaymentNotice : "";
        },

        productCosts(){
            return this.productCostsInfo;
        },

        formattedContractEndDate() {
            return new Date(this.contract.endDate).toLocaleString(
                "default",
                {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                }
            );
        },

        calculateMonthlyListPriceCost() {
            return (this.productCostsInfo.list_price_total / this.licenseTerm);
        }
    },
    methods: {
        ...mapMutations('checkout', [
            'setSelectedPaymentType',
            'setSentPaymentTypeErrorEmail',
            'setRenewalTerm',
            'setLicenseTerm'
        ]),
        ...mapMutations('shoppingCart', [
            'setSoftwareCart',
            'setProductCostsInfo'
        ]),
        ...mapMutations('content', [
            'renewalsPersistedVariables'
        ]),
        formatNumberToCurrency(number) {
            return `${this.currencySymbol}${number.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
        },
        formatMonthlyCost(cost) {
            return `${this.formatNumberToCurrency(cost)}/${this.checkoutContent.selectPaymentInfo.month}`;
        },
        putTextIntoDiv(text) {
            return `<div>${text}</div>`;
        },
        buildSelectedPaymentPlanBreakdown(plan, term=0) {
            if (plan === 'upfront') {
                return `1 ${this.checkoutContent.selectPaymentInfo.upfront_payment} ${this.formatNumberToCurrency(this.productCostsInfo.order_subtotals.upfront)}`;
            } else if (plan ==='monthly') {
                return `${this.checkoutContent.selectPaymentInfo.initial_payment} + ${term} `
                    + `${this.checkoutContent.selectPaymentInfo[plan + '_payments']} ${this.formatNumberToCurrency(this.productCostsInfo.subtotals.software[plan])}`;
            } else {
                return this.buildAnnualPaymentPlanOption(this.checkoutContent.selectPaymentInfo.recurring_cotermed_annual_breakdown, this.checkoutContent.selectPaymentInfo.prorated_cotermed_annual_breakdown);
            }
        },
        renewalPaymentNotice() {
            if (this.selectedPaymentType === 'Upfront Payments') {
                return this.paymentNotice;
            } else if (this.selectedPaymentType === 'Direct Annual') {
                return this.buildAnnualPaymentPlanOption(this.renewalsPersistedVariables.jsonContent.text.recurringCotermedAnnualBreakdown, this.renewalsPersistedVariables.jsonContent.text.proratedCotermedAnnualBreakdown)
            }
            return ""
        },

        buildAnnualPaymentPlanOption(recurringText, proratedText) {
            const numberOfFullAnnualPayments = this.numberofFullAnnualPayments();
            const numberOfMonthsProRated = this.numberOfMonthsProRated();
            const recurringAnnualCost = this.recurringAnnualCost();
            
            // For license terms under a year, prorating has already been accounted for in annual cost
            let proRatedAnnualCost = this.proRatedAnnualCost(numberOfMonthsProRated, numberOfFullAnnualPayments);

            let recurringCostText = recurringText
                .replace('ANNUAL_PAYMENTS', numberOfFullAnnualPayments)
                .replace('RECURRING_ANNUAL_COST', recurringAnnualCost)
            if(numberOfMonthsProRated === 0){
                return this.putTextIntoDiv(recurringCostText);
            }
            let recurringCostDiv = '';
            if (this.licenseTerm >= annualTermLength) {
                if (this.isRenewal) {
                    recurringCostDiv = `<span>${recurringCostText}</span>`;
                } else {
                    recurringCostDiv = `${this.putTextIntoDiv(recurringCostText)}<br/>`;
                }
            }

            let proRatedCostText = proratedText
                .replace('PRORATED_ANNUAL_COST', proRatedAnnualCost)
                .replace('NUMBER_OF_PRORATED_MONTHS', numberOfMonthsProRated);

            const proRatedCostDiv = this.isRenewal ?  `<span>${proRatedCostText}</span><br/>` : this.putTextIntoDiv(proRatedCostText);
            return recurringCostDiv + proRatedCostDiv;
        },

        formatDiscountSavingsText(discountAmount) {
            return `${this.checkoutContent.selectPaymentInfo.save} ${discountAmount}% ${this.checkoutContent.selectPaymentInfo.on_license}`;
        },
        numberofFullAnnualPayments () {
            return Math.floor(this.licenseTerm / 12);
        },
        recurringAnnualCost () {
            return this.formatNumberToCurrency(this.productCostsInfo.subtotals.software.annual);
        }, 
        proRatedAnnualCost (numberOfMonthsProRated, numberOfFullAnnualPayments) {
            if (numberOfFullAnnualPayments === 0) {
                return this.formatNumberToCurrency(this.productCostsInfo.subtotals.software.annual);
            } else {
                const monthlyAnnualCost = this.productCostsInfo.subtotals.software.annual / annualTermLength
                return this.formatNumberToCurrency(monthlyAnnualCost * numberOfMonthsProRated);
            }
        }, 

        numberOfMonthsProRated () {
            return this.licenseTerm % annualTermLength;
        }, 

        buildPaymentPlanOptions() {
            this.paymentPlanOptions['Direct Monthly'].cost = this.productCostsInfo.subtotals.software.monthly;
            this.paymentPlanOptions['Direct Monthly'].monthlyCost = this.formatMonthlyCost(this.paymentPlanOptions['Direct Monthly'].cost);
            
            this.paymentPlanOptions['Direct Monthly'].breakdown = this.buildSelectedPaymentPlanBreakdown('monthly', this.licenseTerm - 1);

            let numberOfMonthsInTerm = this.licenseTerm < annualTermLength ? this.licenseTerm : annualTermLength;
            this.paymentPlanOptions['Direct Annual'].cost = this.productCostsInfo.subtotals.software.annual / numberOfMonthsInTerm;
            this.paymentPlanOptions['Direct Annual'].monthlyCost = this.formatMonthlyCost(this.paymentPlanOptions['Direct Annual'].cost);
            this.paymentPlanOptions['Direct Annual'].breakdown = this.buildSelectedPaymentPlanBreakdown('annual', 2);

            this.paymentPlanOptions['Upfront Payments'].cost = this.productCostsInfo.order_subtotals.upfront / this.licenseTerm;
            this.paymentPlanOptions['Upfront Payments'].monthlyCost = this.formatMonthlyCost( this.paymentPlanOptions['Upfront Payments'].cost);
            this.paymentPlanOptions['Upfront Payments'].breakdown = this.buildSelectedPaymentPlanBreakdown('upfront');

            if (this.checkoutType === 'ExpressCheckout' && !this.isRenewal) {
                this.paymentPlanOptions['Direct Annual'].discount = this.formatDiscountSavingsText(this.expressAnnualDiscount);
                this.paymentPlanOptions['Upfront Payments'].discount = this.formatDiscountSavingsText(this.expressUpfrontDiscount);
            }
        },

        missingPaymentTypeEmailHandler() {
            let response = axios({
                method: 'POST',
                url: '/webstore/send-selected-payment-type-error',
                headers: {'Content-type': 'application/json'},
                data: {},
                timeout: 10000
            });
            return response
        },

        async setSinglePaymentPlan() {
            if (this.isSoftwareCartEmpty || (this.smallFleetOpportunity && this.isAccountCreatedAfterTitan) || this.isPartnerAccount) {
                this.setSelectedPaymentType('Upfront Payments');
            }
            else if (this.smallFleetOpportunity) {
                this.setSelectedPaymentType('Direct Annual');
            } else if (this.checkoutType === 'ExpressCheckout' && this.initialPaymentTerm === 'Net 30' && this.currencyIsoCode === 'USD') {
                this.setSelectedPaymentType('Direct Annual');
            }
            else {
                // If its a MM deal, the oppty already has selectedPaymentType selected so we don't have to set it.
                // Handle the case where an AE didn't select the selectedPaymentType in SFDC
                if (!this.selectedPaymentType) {
                    // Send email if that has not already been triggered
                    if (!this.sentPaymentTypeErrorEmail) {
                        let response = await this.missingPaymentTypeEmailHandler();
                        if (response.data === "Success" && response.status === 200) {
                            this.setSentPaymentTypeErrorEmail(true);
                            this.missingPaymentTypeError = this.checkoutContent.validationErrorMessages.missing_payment_type.text;
                            analyticsTrack('Selected Payment Type Blank Error Email Sent');
                        } else {
                            this.missingPaymentTypeError = this.checkoutContent.validationErrorMessages.missing_payment_type.error;
                        }
                    } else {
                        this.missingPaymentTypeError = this.checkoutContent.validationErrorMessages.missing_payment_type.text;
                    }
                }
            }
        },
        renewalHeader(plan) {
            let header = SFDCPaymentTypeToVariableName[plan];
            return header.charAt(0).toUpperCase() + header.slice(1);
        },
        paymentHeader(plan){
            return this.checkoutContent.selectPaymentInfo[this.paymentPlanOptions[plan].paymentHeader];
        },

        paymentSubHeader(){
            return `${this.licenseTerm} ${this.renewalsPersistedVariables.jsonContent.text.months}`;
        },
        
        handleTermChange(selectedRenewalTerm){
            //analytics tracking
            analyticsTrack('Term Clicked', {"accountId": this.accountId, "opptyId": this.opptyId, "renewalTerm": selectedRenewalTerm, "version": this.applicationVersion, "phase": this.typeOfRenewal, "source": this.productSource});
            this.$emit("toggleTerm", selectedRenewalTerm);
        },
        calculateTotalSavings(plan, numberOfPlans){
            if(plan === 'Direct Monthly' || numberOfPlans < 2){
                return null;
            }

            let monthlySavings = (this.paymentPlanOptions['Direct Monthly'].cost - this.paymentPlanOptions[plan].cost).toFixed(2);
            return this.formatNumberToCurrency(monthlySavings * this.licenseTerm) 
        }
    },
    mounted() {
        if(this.phoenixExptChanges){
            this.setSelectedPaymentType('Upfront Payments');
            this.buildPaymentPlanOptions();
        } else if (!this.shouldShowSinglePlan) {
            this.buildPaymentPlanOptions();
        } else {
            this.setSinglePaymentPlan();
        }
    },
    watch: {
        productCosts: {
            deep: true, 
            handler(){
                this.buildPaymentPlanOptions();
            }
        }
    }

}
</script>

<style scoped>

    .option-container {
        padding-left: 14px;
    }

    .margin-top {
        margin-top: 50px;
    }

    .payment-plan-title {
        font-weight: 500;
        font-size: 24px;
        padding-bottom: 4px;
        border-left: 0px solid;
        border-right: 0px solid;
        border-top: 0px solid;
        color: #00263e;
        border-bottom: 2px solid #f0f0f0;
        padding-left: 0;
        margin-bottom: 20px;
    }

    #payment-notice {
        color: #302f32;
        min-height: 50px;
        padding: 10px;
    }

    #terms-remaining {
        font-size: 18px;
        padding-left: 0;
    }
    
    #contract-disclaimer {
        font-weight: 400;
        color: #00263e;
    }

    .wrapper {
        padding-left: 11px;
    }

    @media only screen and (max-width: 735px) {
        .option-container {
            justify-content: center;
            padding-left: 0;
            flex-wrap: wrap;
        }
    }

    .selected-payment-type-error {
        padding: 15px;
        background-color: #F2F7FF;
        border-radius: 5px;
        border: 1px solid #0384FB;
        margin-left: 11px;
    }

    .selected-payment-type-error p {
        margin-top: 0;
        line-height: 1;
    }

    .toggle-button-left {
        border: 1px solid #C0C9D1;
        border-top-left-radius: 3px;
        border-bottom-left-radius: 3px;
        color: #0D6EDE;
        padding: 10px 40px 10px 40px;
        font-weight: 700;
    }
    .toggle-button-right {
        border: 1px solid #C0C9D1;
        border-top-right-radius: 3px;
        border-bottom-right-radius: 3px;
        color: #0D6EDE;
        padding: 10px 40px 10px 40px;
        font-weight: 700;
    }

    .toggle-button-clicked {
        background: #0D6EDE;
        color: #FFFFFF;
        padding: 10px 40px 10px 40px;
        font-weight: 700;
    }
    .toggle-button {
        padding: 10px 40px 10px 40px;
        font-weight: 700;
    }

</style>
